'use client';

import React, { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

function BookingConfirmationContent() {
  const searchParams = useSearchParams();
  const refParam = searchParams.get('ref');
  const property = searchParams.get('property') || 'Acacia Luxury Apartments';
  const location = searchParams.get('location') || 'Kololo, Kampala, Uganda';
  const checkIn = searchParams.get('checkIn') || '2026-09-10';
  const checkOut = searchParams.get('checkOut') || '2026-09-14';
  const guests = searchParams.get('guests') || '2';
  const total = searchParams.get('total') || '820,000';
  const currency = searchParams.get('currency') || 'UGX';

  const [ref, setRef] = useState<string>(refParam || '');
  const [copied, setCopied] = useState(false);
  const [animIn, setAnimIn] = useState(false);

  useEffect(() => {
    if (!refParam) {
      setRef('NDT-' + Math.random().toString(36).substring(2, 8).toUpperCase());
    }
    const t = setTimeout(() => setAnimIn(true), 100);
    return () => clearTimeout(t);
  }, [refParam]);

  const copyRef = () => {
    navigator.clipboard.writeText(ref).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const nights = (() => {
    const d1 = new Date(checkIn);
    const d2 = new Date(checkOut);
    const diff = Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 4;
  })();

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    return `${days[d.getUTCDay()]}, ${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  };

  const nextSteps = [
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
      ),
      title: 'Check your email',
      desc: 'A confirmation with all details has been sent to your inbox.',
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
      ),
      title: 'Message your host',
      desc: 'Introduce yourself and ask any questions before arrival.',
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
      ),
      title: 'Plan your trip',
      desc: 'Explore nearby attractions, restaurants, and activities.',
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      ),
      title: 'Review cancellation policy',
      desc: 'Free cancellation up to 48 hours before check-in.',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">

          {/* Success banner */}
          <div className={`transition-all duration-700 ${animIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-100 rounded-full mb-4">
                <svg className="w-10 h-10 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Booking Confirmed!</h1>
              <p className="text-gray-500 text-base">Your reservation is all set. Get ready for an amazing stay.</p>
            </div>

            {/* Booking reference card */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 mb-5">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Booking Reference</p>
                  <p className="text-2xl font-bold text-gray-900 font-mono tracking-widest">{ref}</p>
                </div>
                <button
                  onClick={copyRef}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                    copied ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {copied ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                      Copy
                    </>
                  )}
                </button>
              </div>

              {/* Status badge */}
              <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-semibold px-3 py-1.5 rounded-full mb-5">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                Confirmed & Paid
              </div>

              {/* Divider */}
              <div className="border-t border-dashed border-gray-200 my-5" />

              {/* Stay details */}
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">Stay Details</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-rose-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-4.5 h-4.5 text-rose-500 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-medium">Property</p>
                    <p className="text-sm font-semibold text-gray-900">{property}</p>
                    <p className="text-xs text-gray-500">{location}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium">Check-in</p>
                      <p className="text-sm font-semibold text-gray-900">{formatDate(checkIn)}</p>
                      <p className="text-xs text-gray-500">From 2:00 PM</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium">Check-out</p>
                      <p className="text-sm font-semibold text-gray-900">{formatDate(checkOut)}</p>
                      <p className="text-xs text-gray-500">By 11:00 AM</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium">Guests</p>
                      <p className="text-sm font-semibold text-gray-900">{guests} guest{Number(guests) !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-gray-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium">Duration</p>
                      <p className="text-sm font-semibold text-gray-900">{nights} night{nights !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-dashed border-gray-200 my-5" />

              {/* Total */}
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-700">Total paid</p>
                <p className="text-xl font-bold text-gray-900">{currency} {total}</p>
              </div>
            </div>

            {/* Next steps */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 mb-5">
              <h2 className="text-base font-bold text-gray-900 mb-4">What&apos;s next?</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {nextSteps.map((step, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-2xl hover:bg-gray-50 transition-colors">
                    <div className="w-9 h-9 bg-rose-50 rounded-xl flex items-center justify-center flex-shrink-0 text-rose-500">
                      {step.icon}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{step.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="/guest-dashboard"
                className="flex-1 bg-rose-500 hover:bg-rose-600 text-white font-semibold py-3.5 rounded-xl text-center transition-all"
              >
                View My Trips
              </a>
              <a
                href="/messages"
                className="flex-1 border border-gray-200 text-gray-700 font-semibold py-3.5 rounded-xl text-center hover:bg-gray-50 transition-all"
              >
                Message Host
              </a>
              <a
                href="/search"
                className="flex-1 border border-gray-200 text-gray-700 font-semibold py-3.5 rounded-xl text-center hover:bg-gray-50 transition-all"
              >
                Explore More
              </a>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default function BookingConfirmationPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    }>
      <BookingConfirmationContent />
    </Suspense>
  );
}
